import { activatedShare, activationAriaLabel } from "./activation";
import type { ActivationCounts } from "./types";

const RADIUS = 40;
const STROKE = 14;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

type SegmentKey = "activated" | "not-activated" | "unknown";

interface Segment {
  key: SegmentKey;
  label: string;
  value: number;
}

/** Legend/segment rows; Unknown only appears when someone could not be checked. */
export function activationSegments(counts: ActivationCounts): Segment[] {
  const segments: Segment[] = [
    { key: "activated", label: "Has logged in", value: Math.max(0, counts.activated) },
    { key: "not-activated", label: "Not logged in yet", value: Math.max(0, counts.not_activated) }
  ];
  if (counts.unknown > 0) {
    segments.push({ key: "unknown", label: "Unknown", value: counts.unknown });
  }
  return segments;
}

/**
 * Hand-drawn SVG donut of Workvivo logins among active people: Has logged in (green),
 * Not logged in yet (amber) and Unknown (grey, only when > 0). The centre shows the share of
 * active people who have logged in. The legend beside it is real text.
 */
export default function ActivationDonut({ counts, size = 120 }: { counts: ActivationCounts; size?: number }) {
  const pct = activatedShare(counts);
  const segments = activationSegments(counts);
  const drawnTotal = Math.max(
    counts.active,
    segments.reduce((sum, s) => sum + s.value, 0)
  );

  let offset = 0;
  const arcs =
    pct === null || drawnTotal <= 0
      ? []
      : segments
          .filter((s) => s.value > 0)
          .map((s) => {
            const length = (s.value / drawnTotal) * CIRCUMFERENCE;
            const arc = (
              <circle
                key={s.key}
                className={`directory-activation-seg directory-activation-seg--${s.key}`}
                cx="50"
                cy="50"
                r={RADIUS}
                fill="none"
                strokeWidth={STROKE}
                strokeDasharray={`${length} ${CIRCUMFERENCE}`}
                strokeDashoffset={-offset}
              />
            );
            offset += length;
            return arc;
          });

  return (
    <div className="directory-activation-donut">
      <svg
        className="directory-activation-donut-svg"
        role="img"
        aria-label={activationAriaLabel(counts)}
        width={size}
        height={size}
        viewBox="0 0 100 100"
      >
        <circle
          className="directory-activation-track"
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE}
        />
        <g transform="rotate(-90 50 50)">{arcs}</g>
        {pct === null ? null : (
          <>
            <text className="directory-activation-donut-pct" x="50" y="53" textAnchor="middle">
              {pct}%
            </text>
            <text className="directory-activation-donut-caption" x="50" y="66" textAnchor="middle">
              logged in
            </text>
          </>
        )}
      </svg>
      {pct === null ? (
        <p className="directory-activation-legend-empty">No active users</p>
      ) : (
        <ul className="directory-activation-legend" aria-label="Login breakdown">
          {segments.map((s) => (
            <li key={s.key} className="directory-activation-legend-item">
              <span
                className={`directory-activation-swatch directory-activation-swatch--${s.key}`}
                aria-hidden
              />
              <span className="directory-activation-legend-label">{s.label}</span>
              <span className="directory-activation-legend-value">{s.value}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
